import { useCallback } from 'react';
import { type ShopChipElementId, trackShopChipInteraction } from '@pkgs/components/utils/tracking';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { alertVariants } from '@ev-tech/ui-core-components/Alert';
import { COUNTRYCODE_PLACEID_MAP, MarketCountryCode } from '@ev-tech/search-modules-api';

import { useMapStore } from './stores/useMapStore';
import { latLngBoundsToBoundingBox } from '../utils/map';

type RemoveShopFilterWithUndoMessageParams = {
  onClickUndo?: () => void;
  elementId?: ShopChipElementId;
};

export const useShopFilterRemovalMessage = () => {
  const {
    t,
    tracking,
    countryCode,
    options: { language },
  } = useAppContext();
  const { showMessage } = useStatusMessage();

  const resetShopState = useSearchStore((state) => state.resetShopState);
  const setPlaceIds = useSearchStore((state) => state.setPlaceIds);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const applyState = useSearchStore((state) => state.applyState);
  const updateMapBounds = useMapStore((state) => state.updateMapBounds);

  const removeShopFilterWithUndoMessage = useCallback(
    ({ onClickUndo, elementId }: RemoveShopFilterWithUndoMessageParams = {}): void => {
      const previousSearchState = getStateSnapshot();
      const previousMapBounds = window._leaflet_map ? latLngBoundsToBoundingBox(window._leaflet_map.getBounds()) : undefined;

      if (tracking) {
        trackShopChipInteraction({
          elementId: elementId ?? 'lipa-chip_remove',
          tracking,
          language,
          countryCode,
        });
      }

      showMessage(t('search.licencePartner.snackbar.removeFilterMessage'), {
        alertType: alertVariants.info,
        actionText: t('search.licencePartner.snackbar.undo'),
        onAction: () => {
          applyState({ state: previousSearchState });
          if (previousMapBounds) {
            updateMapBounds(previousMapBounds);
          }

          if (tracking) {
            trackShopChipInteraction({
              elementId: 'lipa-chip-snackbar_undo',
              tracking,
              language,
              countryCode,
            });
          }
          onClickUndo?.();
        },
      });
      resetShopState();
      if (
        !previousSearchState.multiPolygon &&
        !previousSearchState.placeIds?.length &&
        countryCode !== MarketCountryCode['private-office']
      ) {
        setPlaceIds([COUNTRYCODE_PLACEID_MAP[countryCode]]);
      }
    },
    [countryCode, language, resetShopState, setPlaceIds, showMessage, t, tracking, applyState, getStateSnapshot, updateMapBounds],
  );

  return { removeShopFilterWithUndoMessage };
};
